import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useSuspenseQueries } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { getLogger } from 'loglevel';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';

import { ErrorIcon, Success } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useBackOrNavigate } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';
import { ServerValidationError } from '@apple/utils/api/errors';
import { decodeSlashes, encodeSlashes } from '@apple/utils/url';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { type ProductKey, productKeySchema } from '@apple/features/product/models/product';
import { categoryQueries } from '@apple/features/product/queries/category';
import { productQueryKeys, productQueryOptions } from '@apple/features/product/queries/management';
import {
	archiveProduct,
	restoreProduct,
	saveProduct,
} from '@apple/features/product-retail/api/management';
import { ProductForm } from '@apple/features/product-retail/components/ProductManagement/ProductForm';
import type { ProductFormData } from '@apple/features/product-retail/models/management';
import { retailProductQueryOptions } from '@apple/features/product-retail/queries/management';
import { PRODUCTS_ODATA_QUERY_KEY } from '@apple/features/product-retail/queries/odata';
import { propagateChanges } from '@apple/features/store-events/api/change-propagation';
import { saveProductVintages } from '@apple/features/vintage/api';

const log = getLogger('manage-product');

export const Route = createFileRoute('/_authed/_admin/manage/products_/$itemId')({
	params: {
		parse: ({ itemId }) => ({
			itemId: productKeySchema.parse(decodeSlashes(itemId)),
		}),
		stringify: ({ itemId }) => ({
			itemId: productKeySchema.parse(encodeSlashes(itemId)),
		}),
	},
	validateSearch: z.object({
		copy: z.boolean().optional(),
	}),
	beforeLoad: ({ params: { itemId }, ...args }) => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.ProductManagement.Write',
		});
		return {
			productQueryOptions: retailProductQueryOptions.product(itemId),
			keywordsQueryOptions: productQueryOptions.keywords,
			productCategoriesQueryOptions: categoryQueries.all,
		};
	},
	loader: ({
		context: {
			queryClient,
			productQueryOptions,
			keywordsQueryOptions,
			productCategoriesQueryOptions,
		},
	}) => {
		void queryClient.prefetchQuery(productQueryOptions);
		void queryClient.prefetchQuery(keywordsQueryOptions);
		void queryClient.prefetchQuery(productCategoriesQueryOptions);
	},
	wrapInSuspense: true,
	component: EditProductRoute,
});

function EditProductRoute() {
	const { t } = useTranslation('product');
	const goBackToList = useBackOrNavigate();
	const { copy: isCopy } = Route.useSearch();
	const {
		productQueryOptions,
		productCategoriesQueryOptions,
		keywordsQueryOptions,
		queryClient,
	} = Route.useRouteContext();

	const [productQuery, productLinesQuery, keywordsQuery] = useSuspenseQueries({
		queries: [productQueryOptions, productCategoriesQueryOptions, keywordsQueryOptions],
	});

	async function handleSuccessfulChange(message: string, goBack = true) {
		void queryClient.invalidateQueries({
			queryKey: [PRODUCTS_ODATA_QUERY_KEY],
		});
		notifications.show({
			message: message,
			icon: <Success />,
			color: 'green',
			autoClose: 5000,
		});
		if (goBack) {
			await goBackToList('/manage/products');
		}
	}

	function showErrorNotification(error: Error) {
		if (!(error instanceof ServerValidationError)) {
			log.error(error);
			notifications.show({
				message: t('common:error.generic'),
				icon: <ErrorIcon />,
				color: 'red',
				autoClose: 5000,
			});
		}
	}

	const updateProductMutation = useMutation({
		mutationFn: async (product: ProductFormData) => {
			await saveProduct(product, 'N/A');
			if (product.vintageIds !== undefined) {
				await saveProductVintages(product.itemId, product.vintageIds);
			}
		},
		onSuccess: async (_, product) => {
			if (product.eventRulePropagations && product.eventRulePropagations.length > 0) {
				await propagateChanges(product.eventRulePropagations, 'N/A');
			}
			void queryClient.invalidateQueries({
				queryKey: productQueryOptions.queryKey,
			});
			const message = isCopy ? t('notifications.created') : t('notifications.updated');
			await handleSuccessfulChange(message, product.goBack ?? true);
		},
		onError: showErrorNotification,
	});

	const archiveProductMutation = useMutation({
		mutationFn: async (itemId: ProductKey) => await archiveProduct(itemId, 'N/A'),
		onSuccess: async () => {
			void queryClient.invalidateQueries({
				queryKey: productQueryKeys.unassignedSkus,
				exact: true,
			});
			void queryClient.invalidateQueries({ queryKey: productQueryKeys.keywords });
			queryClient.removeQueries({
				queryKey: productQueryOptions.queryKey,
			});
			await handleSuccessfulChange(t('notifications.archived'));
		},
		onError: showErrorNotification,
	});

	const restoreProductMutation = useMutation({
		mutationFn: restoreProduct,
		onSuccess: async () => {
			void queryClient.invalidateQueries({
				queryKey: productQueryKeys.unassignedSkus,
				exact: true,
			});
			void queryClient.invalidateQueries({ queryKey: productQueryKeys.keywords });
			queryClient.removeQueries({
				queryKey: productQueryOptions.queryKey,
			});
			await handleSuccessfulChange(t('notifications.restored'), false);
		},
		onError: showErrorNotification,
	});

	const archived = useMemo(() => productQuery.data.isDeleted, [productQuery.data.isDeleted]);

	const renderCopyLink = useCallback(
		() => (
			<Link
				to='/manage/products/$itemId'
				params={{ itemId: productQuery.data.itemId }}
				search={{ copy: true }}
			>
				<Button variant='default'>{t('buttons.copy')}</Button>
			</Link>
		),
		[productQuery.data.itemId, t],
	);

	return (
		<DetailsLayout
			title={
				archived
					? t('titles.viewArchivedProduct', { itemId: productQuery.data?.itemId })
					: isCopy
						? t('titles.copyProduct', { itemId: productQuery.data?.itemId })
						: t('titles.editProduct', { itemId: productQuery.data?.itemId })
			}
		>
			<ProductForm
				isEdit={!isCopy}
				isArchived={archived}
				product={productQuery.data}
				productCategoriesQuery={productLinesQuery}
				keywordsQuery={keywordsQuery}
				saveProductMutation={updateProductMutation}
				archiveProductMutation={archived ? undefined : archiveProductMutation}
				restoreProductMutation={archived ? restoreProductMutation : undefined}
				renderCopyLink={!isCopy ? renderCopyLink : undefined}
			/>
		</DetailsLayout>
	);
}
